import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Classroom } from "@models/ISchema";
import { AppContext } from "../../../context/EditSessionsContext";
import { Types } from "../../../context/switchPage.reducer";
import { Types as FormDataTypes } from "../../../context/formData.reducer";
import FormItem from "../../../components/FormEdit/FormItem";
import ClassroomPicker from "../../../components/classroom-picker";
import { useIsEditorLocked } from "../../../hooks/useIsEditorLocked";
import useClassroomSearch, { ClassroomOption } from "../../../hooks/useClassroomSearch";
import styles from "./classroomSection.module.scss";

interface ClassroomSectionProps {
  disabled?: boolean;
}

const ClassroomSection = ({ disabled: isDisabledByAbility = false }: ClassroomSectionProps) => {
  const { state, dispatch } = useContext(AppContext);
  const isLocked = useIsEditorLocked();
  // The picker follows the lock too, but the permissions message only explains missing abilities.
  const disabled = isDisabledByAbility || isLocked;
  const [isReadOnly, setIsReadOnly] = useState(!state?.form?.isEditionEnabled?.["classrooms"]);
  const { t } = useTranslation();

  const { options, loading, onSearch } = useClassroomSearch();

  const someDeletedSessionIsSelected = useMemo(() => {
    return state?.form?.selectedSessions?.some(session =>
      state?.form?.sessionsToDelete?.hasOwnProperty(session?.id),
    );
  }, [state?.form?.selectedSessions, state?.form?.sessionsToDelete]);

  const handleSelect = (classroom: ClassroomOption) => {
    const alreadyExists = state?.form?.editedSessions?.classrooms?.find(
      item => item.id === classroom.value,
    );

    if (!alreadyExists) {
      const classroomList = state?.form?.editedSessions?.classrooms
        ? [...state?.form?.editedSessions?.classrooms]
        : [];
      classroomList.push(classroom);

      dispatch({
        type: FormDataTypes.ClassroomEditedSessions,
        payload: { classrooms: classroomList },
      });
    }
  };

  const handleDelete = (classroom: Classroom) => {
    const classroomList = [...state?.form?.editedSessions?.classrooms];
    const index = classroomList.findIndex(item => item.id === classroom?.id);
    classroomList.splice(index, 1);

    dispatch({
      type: FormDataTypes.ClassroomEditedSessions,
      payload: { classrooms: classroomList },
    });
  };

  const handleAdvancedSearch = () => {
    dispatch({
      type: Types.AdvanceSearchClassroom,
      payload: true,
    });
  };

  return (
    <FormItem
      title={t("assignation-edit.form.classroom.title")}
      type="classrooms"
      isDisabled={isDisabledByAbility}
      onShowReadOnlyContent={setIsReadOnly}
    >
      <motion.div
        className={styles.classrooms}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ type: "spring", duration: 0.3, bounce: 0.25 }}
      >
        <ClassroomPicker
          classrooms={state?.form?.editedSessions?.classrooms ?? []}
          disabled={disabled}
          loading={loading}
          options={options}
          someDeletedSessionIsSelected={someDeletedSessionIsSelected}
          isReadOnly={isReadOnly}
          onSearch={onSearch}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onAdvancedSearch={handleAdvancedSearch}
        />
      </motion.div>
    </FormItem>
  );
};

export default ClassroomSection;
